/**
 * AddToHomeScreenBanner — iter87.210.
 *
 * Small dismissible bottom banner shown on the Pro+ dashboard nudging
 * mobile users to save the app to their home screen. Never shown:
 *   - on desktop (no meaningful "home screen" concept there)
 *   - if the app is already running in standalone/home-screen mode
 *   - after the user dismisses it once (persisted in localStorage)
 *
 * Android/Chrome: captures the native `beforeinstallprompt` event so
 * the button triggers a real one-tap install when available.
 * iOS Safari: no such event exists — shows manual Share-sheet steps.
 */
import { useEffect, useState } from "react";
import { X, Share, Menu as MenuIcon } from "lucide-react";

const DISMISS_KEY = "tcr.a2hs_dismissed_v1";

function isStandalone() {
    return (
        window.matchMedia?.("(display-mode: standalone)")?.matches ||
        window.navigator?.standalone === true
    );
}

function isMobileUA() {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

function isIOS() {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
}

export default function AddToHomeScreenBanner() {
    const [visible, setVisible] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const ios = isIOS();

    useEffect(() => {
        if (isStandalone() || !isMobileUA()) return;
        try {
            if (localStorage.getItem(DISMISS_KEY) === "1") return;
        } catch { /* storage disabled — show it anyway */ }
        setVisible(true);
    }, []);

    useEffect(() => {
        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
    }, []);

    const dismiss = () => {
        setVisible(false);
        try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* storage disabled */ }
    };

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            await deferredPrompt.userChoice;
            setDeferredPrompt(null);
        }
        dismiss();
    };

    if (!visible) return null;

    return (
        <div
            data-testid="add-to-home-screen-banner"
            className="fixed bottom-0 inset-x-0 z-50 p-3 sm:p-4 animate-in slide-in-from-bottom duration-300"
        >
            <div className="max-w-md mx-auto brutal-card p-4 bg-[#121212] border-cyber/50 flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyber/10 border border-cyber/40 flex items-center justify-center shrink-0">
                    {ios ? <Share className="w-4 h-4 text-cyber" /> : <MenuIcon className="w-4 h-4 text-cyber" />}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-display font-extrabold text-sm uppercase tracking-tight">
                        Add Pro+ to your home screen
                    </p>
                    <p className="text-[11px] text-zinc-400 mt-0.5">
                        {ios
                            ? "Tap the Share icon, then \"Add to Home Screen\" for one-tap access."
                            : deferredPrompt
                                ? "Install it for one-tap access, no browser bar."
                                : "Tap the menu, then \"Add to Home Screen\" for one-tap access."}
                    </p>
                    {!ios && deferredPrompt && (
                        <button
                            type="button"
                            onClick={handleInstallClick}
                            data-testid="add-to-home-screen-install-btn"
                            className="brutal-btn text-xs py-1.5 px-3 mt-2 bg-cyber/15 border-cyber text-cyber"
                        >
                            Install
                        </button>
                    )}
                </div>
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label="Dismiss"
                    data-testid="add-to-home-screen-dismiss-btn"
                    className="text-zinc-500 hover:text-white shrink-0 p-1"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
